import React, { useState, useEffect, useRef, useCallback } from "react"
import { useSelector } from "react-redux"
import moment from "moment"
import { getProductionStepsSitesSelector } from "../../reducers/Production/ProductionSteps"
import { PROD_PLANNER_FILTER_DATE } from "../../utils/constant"
import ProdPlannerHeader from "../prodPlanner/ProdPlannerHeader"

const ProdPlannerLayout = ({ children, location }) => {
    const sites = useSelector(getProductionStepsSitesSelector)
    
    // Refs to store the actual handler functions from child
    const cancelHandlerRef = useRef(null)
    const saveHandlerRef = useRef(null)
    const coolingHandlerRef = useRef(null)

    // Track if child has unsaved changes
    const [hasChanges, setHasChanges] = useState(false)
    const [coolingButtonState, setCoolingButtonState] = useState({ show: false, disabled: true })

    // Get initial date from sessionStorage if available, otherwise use today
    const getInitialDate = () => {
        try {
            const storedDate = sessionStorage.getItem(PROD_PLANNER_FILTER_DATE)
            if (storedDate) {
                const parsedDate = parseInt(storedDate)
                if (!isNaN(parsedDate)) return parsedDate
            }
        } catch (error) {
            console.error("Error reading date from sessionStorage:", error)
        }
        // Fallback to today if no valid stored date
        return moment.utc().startOf("day").valueOf()
    }

    // Local state for the date that updates in real-time
    const [selectedDate, setSelectedDate] = useState(getInitialDate)

    // Local state for the kitchen area that updates in real-time
    const kitchenAreas = sites.reduce((acc, site) => { return [...acc, ...site.kitchenAreas] }, [])
    const [selectedKitchenArea, setSelectedKitchenArea] = useState(() => kitchenAreas.find(kitchenArea => kitchenArea.name === "Prépa chaude") || kitchenAreas[0] || "")

    // Save selected date to sessionStorage whenever it changes
    // sessionStorage persists across page refreshes but clears when browser/tab closes
    useEffect(() => {
        try {
            sessionStorage.setItem(PROD_PLANNER_FILTER_DATE, selectedDate.toString())
        } catch (error) {
            console.error("Error saving date to sessionStorage:", error)
        }
    }, [selectedDate])

    // Callbacks that child will use to register its handlers
    const onCancelRequest = useCallback((handler) => {
        cancelHandlerRef.current = handler
    }, [])

    const onSaveRequest = useCallback((handler) => {
        saveHandlerRef.current = handler
    }, [])

    const onCoolingRequest = useCallback((handler) => {
        coolingHandlerRef.current = handler
    }, [])

    // Callback for child to update hasChanges state
    const onHasChangesUpdate = useCallback((value) => {
        setHasChanges(value)
    }, [])

    const onCoolingButtonStateChange = useCallback((state) => {
        setCoolingButtonState(state)
    }, [])

    // Handlers for cancel, save and cooling actions that call the registered child handlers
    const handleCancel = () => {
        if (cancelHandlerRef.current) {
            cancelHandlerRef.current()
        }
    }

    const handleSave = async () => {
        if (saveHandlerRef.current) {
            await saveHandlerRef.current()
        }
    }

    const handleCooling = () => {
        if (coolingHandlerRef.current) {
            coolingHandlerRef.current()
        }
    }

    return (
        <div style={useStyles.headerBlock}>
            <ProdPlannerHeader
                pathname={location.pathname}
                selectedDate={selectedDate}
                selectedKitchenArea={selectedKitchenArea}
                onCancel={hasChanges ? handleCancel : null}
                onSave={handleSave}
                onCooling={handleCooling}
                showCoolingButton={coolingButtonState.show}
                coolingDisabled={coolingButtonState.disabled}
            />
            {React.Children.map(children, child =>
                child ? React.cloneElement(child, {
                    selectedDate,
                    setSelectedDate,
                    selectedKitchenArea,
                    setSelectedKitchenArea,
                    onCancelRequest,
                    onSaveRequest,
                    onCoolingRequest,
                    onHasChangesUpdate,
                    onCoolingButtonStateChange
                }) : null
            )}
        </div>
    )
}

export default ProdPlannerLayout

const useStyles = {
    headerBlock: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%"
    }
}